import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from '../../admin/app';
import LoadingIndicator from '../../common/components/LoadingIndicator';
import Button from '../../common/components/Button';
import Dropdown from '../../common/components/Dropdown';
import listItems from '../../common/helpers/listItems';
import ItemList from '../../common/utils/ItemList';
import classList from '../../common/utils/classList';
import extractText from '../../common/utils/extractText';
import AdminPage from './AdminPage';
import { debounce } from '../../common/utils/throttleDebounce';
import CreateUserModal from './CreateUserModal';
import Icon from '../../common/components/Icon';
import Input from '../../common/components/Input';
import GambitsAutocompleteDropdown from '../../common/components/GambitsAutocompleteDropdown';
import Pagination from '../../common/components/Pagination';
/**
 * Admin page which displays a paginated list of all users on the forum.
 */
export default class UserListPage extends AdminPage {
  constructor() {
    super(...arguments);
    _defineProperty(this, "query", '');
    _defineProperty(this, "throttledSearch", debounce(250, () => this.loadPage(0)));
    /**
     * Number of users to load per page.
     */
    _defineProperty(this, "numPerPage", 50);
    /**
     * Current page number. Zero-indexed.
     */
    _defineProperty(this, "pageNumber", 0);
    /**
     * Page number being loaded. Zero-indexed.
     */
    _defineProperty(this, "loadingPageNumber", 0);
    /**
     * Total number of forum users.
     *
     * Fetched from the active `AdminApplication` (`app`), with
     * data provided by `AdminPayload.php`, or `flarum/statistics`
     * if installed.
     */
    _defineProperty(this, "userCount", app.data.modelStatistics.users.total);
    /**
     * This page's array of users.
     *
     * `undefined` when page loads as no data has been fetched.
     */
    _defineProperty(this, "pageData", undefined);
    _defineProperty(this, "isLoadingPage", false);
  }
  /**
   * Get total number of user pages.
   */
  getTotalPageCount() {
    if (this.userCount === -1) return 0;
    return Math.ceil(this.userCount / this.numPerPage);
  }
  oninit(vnode) {
    super.oninit(vnode);

    // Get page query value from URL
    const page = parseInt(m.route.param('page'));
    if (isNaN(page) || page < 1) {
      this.setPageNumberInUrl(1);
      this.pageNumber = 0;
    } else {
      this.pageNumber = page - 1;
    }
    this.loadingPageNumber = this.pageNumber;
  }

  /**
   * Component to render.
   */
  content() {
    if (typeof this.pageData === 'undefined') {
      this.loadPage(this.pageNumber);
      return [m("section", {
        className: "UserListPage-grid UserListPage-grid--loading"
      }, m(LoadingIndicator, {
        containerClassName: "LoadingIndicator--block",
        size: "large"
      }))];
    }
    const columns = this.columns().toArray();
    return [m("div", {
      className: "UserListPage-header"
    }, this.headerItems().toArray()), m("section", {
      className: classList(['UserListPage-grid', this.isLoadingPage ? 'UserListPage-grid--loadingPage' : 'UserListPage-grid--loaded']),
      style: {
        '--columns': columns.length
      },
      role: "table"
      // +1 to account for header
      ,
      "aria-rowcount": this.pageData.length + 1,
      "aria-colcount": columns.length,
      "aria-live": "polite",
      "aria-busy": this.isLoadingPage ? 'true' : 'false'
    }, columns.map((column, colIndex) => m("div", {
      className: "UserListPage-grid-header",
      role: "columnheader",
      "aria-colindex": colIndex + 1,
      "aria-rowindex": 1
    }, column.name)), this.pageData.map((user, rowIndex) => columns.map((col, colIndex) => {
      const columnContent = col.content && col.content(user);
      return m("div", {
        className: classList(['UserListPage-grid-rowItem', rowIndex % 2 > 0 && 'UserListPage-grid-rowItem--shaded']),
        "data-user-id": user.id(),
        "data-column-name": col.itemName,
        "aria-colindex": colIndex + 1
        // +2 to account for 0-based index, and for the header row
        ,
        "aria-rowindex": rowIndex + 2,
        role: "cell"
      }, columnContent || app.translator.trans('core.admin.users.grid.invalid_column_content'));
    })), this.isLoadingPage && m(LoadingIndicator, {
      size: "large"
    })), m(Pagination, {
      currentPage: this.pageNumber + 1,
      loadingPageNumber: this.loadingPageNumber + 1,
      total: this.userCount,
      perPage: this.numPerPage,
      onChange: this.goToPage.bind(this)
    })];
  }
  headerItems() {
    const items = new ItemList();
    const onchange = value => {
      this.isLoadingPage = true;
      this.query = value;
      this.throttledSearch();
    };
    items.add('search', m(GambitsAutocompleteDropdown, {
      resource: "users",
      query: this.query,
      onchange: onchange
    }, m(Input, {
      type: "search",
      placeholder: app.translator.trans('core.admin.users.search_placeholder'),
      clearable: true,
      loading: this.isLoadingPage,
      value: this.query,
      onchange: onchange
    })), 100);
    items.add('totalUsers', m("p", {
      class: "UserListPage-totalUsers"
    }, app.translator.trans('core.admin.users.total_users', {
      count: this.userCount
    })), 90);
    items.add('actions', m("div", {
      className: "UserListPage-actions"
    }, this.actionItems().toArray()), 80);
    return items;
  }
  actionItems() {
    const items = new ItemList();
    items.add('createUser', m(Button, {
      className: "Button UserListPage-createUserBtn",
      icon: "fas fa-user-plus",
      onclick: () => app.modal.show(CreateUserModal)
    }, app.translator.trans('core.admin.users.create_user_button')), 100);
    return items;
  }

  /**
   * Build an item list of columns to show for each user.
   *
   * Each column in the list should be an object with keys `name` and `content`.
   *
   * `name` is a string that will be used as the column name.
   * `content` is a function with the User model passed as the first and only argument.
   *
   * See `UserListPage.tsx` for examples.
   */
  columns() {
    const columns = new ItemList();
    columns.add('id', {
      name: app.translator.trans('core.admin.users.grid.columns.user_id.title'),
      content: user => {
        var _user$id;
        return (_user$id = user.id()) != null ? _user$id : null;
      }
    }, 100);
    columns.add('username', {
      name: app.translator.trans('core.admin.users.grid.columns.username.title'),
      content: user => {
        const profileUrl = "".concat(app.forum.attribute('baseUrl'), "/u/").concat(user.slug());
        return m("a", {
          target: "_blank",
          href: profileUrl,
          title: extractText(app.translator.trans('core.admin.users.grid.columns.username.profile_link_tooltip', {
            username: user.username()
          }))
        }, user.username());
      }
    }, 90);
    columns.add('displayName', {
      name: app.translator.trans('core.admin.users.grid.columns.display_name.title'),
      content: user => user.displayName()
    }, 85);
    columns.add('joinDate', {
      name: app.translator.trans('core.admin.users.grid.columns.join_time.title'),
      content: user => m("span", {
        className: "UserList-joinDate",
        title: user.joinTime()
      }, app.translator.formatDateTime(dayjs(user.joinTime()), 'core.lib.datetime_formats.userListJoinDate'))
    }, 80);
    columns.add('groupBadges', {
      name: app.translator.trans('core.admin.users.grid.columns.group_badges.title'),
      content: user => {
        const badges = user.badges().toArray();
        if (badges.length) {
          return m("ul", {
            className: "DiscussionHero-badges badges"
          }, listItems(badges));
        } else {
          return app.translator.trans('core.admin.users.grid.columns.group_badges.no_badges');
        }
      }
    }, 70);
    columns.add('emailAddress', {
      name: app.translator.trans('core.admin.users.grid.columns.email.title'),
      content: user => {
        function setEmailVisibility(visible) {
          // Get needed jQuery element refs
          const emailContainer = $("[data-column-name=emailAddress][data-user-id=".concat(user.id(), "] .UserList-email"));
          const emailAddress = emailContainer.find('.UserList-emailAddress');
          const emailToggleButton = emailContainer.find('.UserList-emailIconBtn');
          const emailToggleButtonIcon = emailToggleButton.find('.icon');
          emailToggleButton.attr('title', extractText(visible ? app.translator.trans('core.admin.users.grid.columns.email.visibility_hide') : app.translator.trans('core.admin.users.grid.columns.email.visibility_show')));
          emailAddress.attr('aria-hidden', visible ? null : 'true');
          if (visible) {
            emailToggleButtonIcon.addClass('fa-eye');
            emailToggleButtonIcon.removeClass('fa-eye-slash');
          } else {
            emailToggleButtonIcon.removeClass('fa-eye');
            emailToggleButtonIcon.addClass('fa-eye-slash');
          }

          // Need the string interpolation to prevent TS error.
          emailContainer.attr('data-email-shown', "".concat(visible));
        }
        function toggleEmailVisibility() {
          const emailContainer = $("[data-column-name=emailAddress][data-user-id=".concat(user.id(), "] .UserList-email"));
          const emailShown = emailContainer.attr('data-email-shown') === 'true';
          if (emailShown) {
            setEmailVisibility(false);
          } else {
            setEmailVisibility(true);
          }
        }
        return m("div", {
          className: "UserList-email",
          key: user.id(),
          "data-email-shown": "false"
        }, m("span", {
          className: "UserList-emailAddress",
          "aria-hidden": "true",
          onclick: () => setEmailVisibility(true)
        }, user.email()), m("button", {
          onclick: toggleEmailVisibility,
          className: "Button Button--text UserList-emailIconBtn",
          type: "button",
          title: app.translator.trans('core.admin.users.grid.columns.email.visibility_show')
        }, m(Icon, {
          name: "far fa-eye-slash fa-fw",
          className: "icon"
        })));
      }
    }, 70);
    columns.add('userActions', {
      name: app.translator.trans('core.admin.users.grid.columns.user_actions.title'),
      content: user => m(Dropdown, {
        className: "UserList-userActions",
        buttonClassName: "Button UserList-userActionsBtn Button--icon Button--flat",
        menuClassName: "Dropdown-menu--right",
        icon: "fas fa-ellipsis-h"
      }, this.userActionItems(user).toArray())
    }, -90);
    return columns;
  }
  userActionItems(user) {
    const items = new ItemList();
    items.add('editUser', m(Button, {
      icon: "fas fa-pencil-alt",
      title: app.translator.trans('core.admin.users.grid.columns.user_actions.edit_user.tooltip', {
        username: user.displayName()
      }, true),
      onclick: () => app.modal.show(() => import(/* webpackChunkName: 'common/components/EditUserModal', webpackMode: 'lazy-once' */ '../../common/components/EditUserModal'), {
        user
      })
    }, app.translator.trans('core.admin.users.grid.columns.user_actions.edit_user.button')));
    return items;
  }
  headerInfo() {
    return {
      className: 'UserListPage',
      icon: 'fas fa-users',
      title: app.translator.trans('core.admin.users.title'),
      description: app.translator.trans('core.admin.users.description')
    };
  }

  /**
   * Asynchronously fetch the next set of users to be rendered.
   *
   * Returns an array of Users, plus the raw API payload.
   *
   * Uses the `this.numPerPage` as the response limit, and automatically calculates the offset required from `pageNumber`.
   *
   * @param pageNumber The **zero-based** page number to load and display
   */
  async loadPage(pageNumber) {
    if (pageNumber < 0) pageNumber = 0;
    this.loadingPageNumber = pageNumber;
    this.setPageNumberInUrl(pageNumber + 1);
    app.store.find('users', {
      filter: {
        q: this.query
      },
      page: {
        limit: this.numPerPage,
        offset: pageNumber * this.numPerPage
      }
    }).then(apiData => {
      let data = apiData;

      // @ts-ignore
      delete data.payload;
      const lastPage = this.getTotalPageCount();
      if (pageNumber > lastPage) {
        this.loadPage(lastPage - 1);
      } else {
        this.pageData = data;
        this.pageNumber = pageNumber;
        this.loadingPageNumber = pageNumber;
        this.isLoadingPage = false;
      }
      m.redraw();
    }).catch(err => {
      console.error(err);
      this.pageData = [];
    });
  }

  /**
   * @param page The **1-based** page number
   */
  goToPage(page) {
    this.isLoadingPage = true;
    this.loadPage(page - 1);
  }
  setPageNumberInUrl(pageNumber) {
    var _search$;
    const search = window.location.hash.split('?', 2);
    const params = new URLSearchParams((_search$ = search == null ? void 0 : search[1]) != null ? _search$ : '');
    params.set('page', "".concat(pageNumber));
    // window.location.hash = search?.[0] + '?' + params.toString();
    window.history.replaceState(null, '', (search == null ? void 0 : search[0]) + '?' + params.toString());
  }
}
flarum.reg.add('core', 'admin/components/UserListPage', UserListPage);flarum.reg.addChunkModule('234', '422', 'core', 'common/components/EditUserModal');